/**
 * Public Interview Chat Page.
 *
 * Standalone full-page conversational chat with the AI interviewer.
 * No sidebar, no auth, no platform chrome.
 * Accessed via /interview/:token
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, CheckCircle, User, ChevronRight, ChevronDown, Check, Circle, Clock, Mail, PanelRightOpen, PanelRightClose } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Markdown } from '@/components/common/Markdown';
import { ThemeToggle } from '@/components/ThemeToggle';
import { usePublicInterview } from '@/hooks/usePublicInterview';
import type { ChatMessage } from '@/hooks/usePublicInterview';
import type { TopicInfo, KnowledgeDelta } from '@/lib/api/publicInterview';
import { cn } from '@/lib/utils';
import { ConfidenceRing, confidenceColor } from '@/components/interview/ConfidenceRing';
import { HobbesAvatar } from '@/components/interview/HobbesAvatar';
import { ReviewView } from '@/components/interview/ReviewView';
import { KnowledgeSidebar } from '@/components/interview/KnowledgeSidebar';
import { KnowledgeUpdateBanner } from '@/components/interview/KnowledgeUpdateBanner';

// ========================================
// ANIMATION VARIANTS
// ========================================

const messageVariantsLeft = {
  initial: { opacity: 0, y: 16, x: -12 },
  animate: { opacity: 1, y: 0, x: 0 },
  exit: { opacity: 0, y: -10 },
};

const messageVariantsRight = {
  initial: { opacity: 0, y: 16, x: 12 },
  animate: { opacity: 1, y: 0, x: 0 },
  exit: { opacity: 0, y: -10 },
};

const chipContainerVariants = {
  initial: {},
  animate: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const chipVariants = {
  initial: { opacity: 0, y: 8, scale: 0.95 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const easeOut = { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const };

// ========================================
// TYPING INDICATOR (bouncing dots)
// ========================================

const TypingIndicator: React.FC = () => (
  <div className="flex items-center gap-1.5 py-1 px-0.5">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40"
        style={{
          animation: 'typing-fade 1.8s ease-in-out infinite',
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>{`
      @keyframes typing-fade {
        0%, 60%, 100% { opacity: 0.25; }
        30% { opacity: 0.8; }
      }
    `}</style>
  </div>
);

// ========================================
// BLINKING CURSOR (for streaming)
// ========================================

const StreamingCursor: React.FC = () => (
  <>
    <span
      className="inline-block w-0.5 h-4 bg-primary/60 ml-0.5 align-middle"
      style={{ animation: 'cursor-blink 1s steps(2) infinite' }}
    />
    <style>{`
      @keyframes cursor-blink {
        0% { opacity: 1; }
        50% { opacity: 0; }
        100% { opacity: 1; }
      }
    `}</style>
  </>
);

// ========================================
// CHAT HEADER WITH PROGRESS
// ========================================

const ChatHeader: React.FC<{
  name?: string | null;
  logoUrl?: string | null;
  totalTopics?: number;
  currentTopicIndex?: number;
  topics?: TopicInfo[];
  topicConfidence?: Record<string, number>;
  onNavigateToTopic?: (index: number) => void;
  onComplete?: () => void;
  onSkipTopic?: () => void;
  isNavigating?: boolean;
  isComplete?: boolean;
  isStreaming?: boolean;
  isCompleting?: boolean;
  isReviewing?: boolean;
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  hasSidebarKnowledge?: boolean;
}> = ({
  name, logoUrl, totalTopics = 0, currentTopicIndex = 0, topics = [],
  topicConfidence = {}, onNavigateToTopic, onComplete, onSkipTopic,
  isNavigating, isComplete, isStreaming, isCompleting, isReviewing,
  sidebarOpen, onToggleSidebar, hasSidebarKnowledge,
}) => {
  const [topicMenuOpen, setTopicMenuOpen] = useState(false);

  const handleTopicClick = (index: number) => {
    if (index !== currentTopicIndex && onNavigateToTopic) {
      onNavigateToTopic(index);
    }
    setTopicMenuOpen(false);
  };

  const currentTopic = topics[currentTopicIndex];
  const currentConf = currentTopic ? (topicConfidence[currentTopic.id] ?? 0) : 0;
  const currentPct = Math.round(currentConf * 100);

  return (
    <header className="bg-card shrink-0 border-b border-border">
      <div className="px-4 py-3 flex items-center gap-3">
        <HobbesAvatar size="sm" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold text-foreground">
            {name || 'Hobbes'}
          </h1>
          {totalTopics > 0 && topics.length > 0 && !isReviewing ? (
            <Popover open={topicMenuOpen} onOpenChange={setTopicMenuOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  <span>
                    Topic {Math.min(currentTopicIndex + 1, totalTopics)} of {totalTopics}
                    {topics[currentTopicIndex] && ` — ${topics[currentTopicIndex].name}`}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                  {currentTopic && <ConfidenceRing value={currentConf} />}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 p-1.5">
                <div className="space-y-0.5">
                  {topics.map((topic) => {
                    const isCurrent = topic.index === currentTopicIndex;
                    const isVisited = topic.index < currentTopicIndex;
                    const conf = topicConfidence[topic.id] ?? 0;
                    const pct = Math.round(conf * 100);
                    return (
                      <button
                        key={topic.id}
                        onClick={() => handleTopicClick(topic.index)}
                        disabled={isNavigating}
                        className={cn(
                          'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-sm transition-colors',
                          isCurrent
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-muted',
                          isNavigating && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {isVisited ? (
                          <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        ) : isCurrent ? (
                          <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="truncate block">{topic.name}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                              <motion.div
                                className={cn('h-full rounded-full', confidenceColor(conf))}
                                initial={{ width: 0 }}
                                animate={{ width: `${pct}%` }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                              />
                            </div>
                            <span className="text-[10px] text-muted-foreground tabular-nums w-7 text-right">
                              {pct}%
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <p className="text-xs text-muted-foreground">
              {totalTopics > 0
                ? `Topic ${Math.min(currentTopicIndex + 1, totalTopics)} of ${totalTopics}`
                : 'Hobbes Interview'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isComplete && !isReviewing && onSkipTopic && totalTopics > 0 && currentTopicIndex < totalTopics - 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSkipTopic}
              disabled={isStreaming || isNavigating}
              className="text-xs h-7 px-2.5 rounded-md"
            >
              Next Topic
              <ChevronRight className="h-3 w-3 ml-0.5" />
            </Button>
          )}
          {!isComplete && !isReviewing && onComplete && (
            <Button
              variant="outline"
              size="sm"
              onClick={onComplete}
              disabled={isStreaming || isNavigating || isCompleting}
              className="text-xs h-7 px-2.5 rounded-md"
            >
              {isCompleting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                'Review and Finish'
              )}
            </Button>
          )}
          {hasSidebarKnowledge && !isComplete && !isReviewing && onToggleSidebar && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSidebar}
              className="text-xs h-7 w-7 p-0 rounded-md"
              title={sidebarOpen ? 'Hide knowledge' : 'Show knowledge'}
            >
              {sidebarOpen ? (
                <PanelRightClose className="h-3.5 w-3.5" />
              ) : (
                <PanelRightOpen className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};

// ========================================
// MESSAGE BUBBLE
// ========================================

const MessageBubble: React.FC<{
  message: ChatMessage;
  logoUrl?: string | null;
  onSelectSuggestion?: (answer: string) => void;
  onSubmitMultiSelect?: (answers: string[]) => void;
  isLastMessage?: boolean;
  onViewKnowledgeChanges?: (delta: KnowledgeDelta) => void;
}> = ({ message, logoUrl, onSelectSuggestion, onSubmitMultiSelect, isLastMessage, onViewKnowledgeChanges }) => {
  const isInterviewer = message.speaker === 'interviewer';
  const isMultiSelect = message.suggestionSelectionType === 'multi';
  const [selectedAnswers, setSelectedAnswers] = useState<Set<string>>(new Set());
  const suggestionSignature = message.suggestedAnswers?.join('||') ?? '';

  // Prevent stale selections if the backend updates the suggestion list for this message.
  useEffect(() => {
    setSelectedAnswers(new Set());
  }, [message.id, message.suggestionSelectionType, suggestionSignature]);

  const toggleAnswer = useCallback((answer: string) => {
    setSelectedAnswers((prev) => {
      const next = new Set(prev);
      if (next.has(answer)) {
        next.delete(answer);
      } else {
        next.add(answer);
      }
      return next;
    });
  }, []);

  const handleSubmitMulti = useCallback(() => {
    if (selectedAnswers.size > 0 && onSubmitMultiSelect) {
      onSubmitMultiSelect(Array.from(selectedAnswers));
      setSelectedAnswers(new Set());
    }
  }, [selectedAnswers, onSubmitMultiSelect]);

  const showSuggestions =
    isInterviewer &&
    isLastMessage &&
    message.suggestedAnswers &&
    message.suggestedAnswers.length > 0 &&
    !message.isStreaming;

  return (
    <motion.div
      variants={isInterviewer ? messageVariantsLeft : messageVariantsRight}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={easeOut}
      className={cn('flex gap-3', !isInterviewer && 'flex-row-reverse')}
    >
      {/* Avatar */}
      {isInterviewer ? (
        <HobbesAvatar size="sm" />
      ) : (
        logoUrl ? (
          <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden mt-0.5">
            <img src={logoUrl} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary text-muted-foreground flex items-center justify-center mt-0.5">
            <User className="h-4 w-4" />
          </div>
        )
      )}

      {/* Content */}
      <div className={cn('flex-1 max-w-[80%] space-y-2')}>
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5',
            isInterviewer
              ? 'bg-card text-foreground rounded-tl-md shadow-sm border border-border'
              : 'bg-primary text-primary-foreground rounded-tr-md'
          )}
        >
          {isInterviewer ? (
            <div>
              {/* Typing indicator when streaming but no content yet */}
              {message.isStreaming && !message.content ? (
                <TypingIndicator />
              ) : (
                <>
                  <Markdown
                    content={message.content}
                    className="text-sm leading-relaxed"
                  />
                  {message.isStreaming && message.content && <StreamingCursor />}
                </>
              )}
              {/* Knowledge update banner inside bubble */}
              {!message.isStreaming && message.knowledgeDelta && onViewKnowledgeChanges && (
                <div className="mt-2 -mx-1">
                  <KnowledgeUpdateBanner
                    delta={message.knowledgeDelta}
                    onViewChanges={onViewKnowledgeChanges}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          )}
        </div>

        {/* Suggested answer chips */}
        {showSuggestions && (
          <motion.div
            variants={chipContainerVariants}
            initial="initial"
            animate="animate"
            className="flex flex-wrap gap-2 pl-1"
          >
            {isMultiSelect && (
              <motion.p
                variants={chipVariants}
                className="w-full text-xs text-muted-foreground mb-0.5"
              >
                Select all that apply
              </motion.p>
            )}
            {message.suggestedAnswers!.map((answer, i) => {
              const isSelected = selectedAnswers.has(answer);
              return isMultiSelect ? (
                <motion.button
                  key={i}
                  variants={chipVariants}
                  whileHover={{ scale: 1.03, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => toggleAnswer(answer)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border transition-colors cursor-pointer',
                    isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-primary/30 text-primary bg-card hover:bg-primary/5 hover:border-primary/50'
                  )}
                >
                  {isSelected ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Circle className="h-3 w-3 opacity-40" />
                  )}
                  {answer}
                </motion.button>
              ) : (
                <motion.button
                  key={i}
                  variants={chipVariants}
                  whileHover={{ scale: 1.03, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectSuggestion?.(answer)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border border-primary/30 text-primary bg-card hover:bg-primary/5 hover:border-primary/50 transition-colors cursor-pointer"
                >
                  {answer}
                  <ChevronRight className="h-3 w-3 opacity-50" />
                </motion.button>
              );
            })}
            {isMultiSelect && selectedAnswers.size > 0 && (
              <motion.button
                variants={chipVariants}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSubmitMulti}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <Send className="h-3 w-3" />
                Send {selectedAnswers.size} selected
              </motion.button>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

// ========================================
// CHAT INPUT
// ========================================

const ChatInput: React.FC<{
  onSend: (content: string) => void;
  disabled: boolean;
}> = ({ onSend, disabled }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    const content = value.trim();
    if (!content || disabled) return;
    setValue('');
    onSend(content);
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Focus input when enabled
  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  return (
    <div className="px-4 py-3 shrink-0">
      <div className="max-w-[720px] mx-auto flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Hobbes is thinking...' : 'Type your answer...'}
          disabled={disabled}
          className="flex-1 rounded-full border border-input px-4 py-2.5 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:opacity-50 disabled:cursor-not-allowed bg-background"
        />
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!value.trim() || disabled}
            className="rounded-full h-10 w-10 p-0"
          >
            {disabled ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

// ========================================
// COMPLETION VIEW
// ========================================

const CompletionView: React.FC<{
  summary: string | null;
  isCompleting: boolean;
  onComplete: () => void;
  alreadyComplete: boolean;
}> = ({ summary, isCompleting, onComplete, alreadyComplete }) => {
  if (summary) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={easeOut}
        className="flex-1 overflow-y-auto"
      >
        <div className="max-w-[720px] mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            >
              <CheckCircle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            </motion.div>
            <h2 className="text-lg font-semibold text-foreground">
              Interview Complete
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your responses have been recorded. Here's a summary.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-sm text-foreground leading-relaxed max-h-[50vh] overflow-y-auto">
              <Markdown content={summary} className="prose-sm" />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!alreadyComplete) {
    return null;
  }

  return (
    <div className="border-t border-border bg-card px-4 py-3 shrink-0">
      <div className="max-w-[720px] mx-auto text-center">
        <Button onClick={onComplete} disabled={isCompleting}>
          {isCompleting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating Summary...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Complete Interview
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

// ========================================
// SKELETON LOADING
// ========================================

const ChatSkeleton: React.FC = () => (
  <div className="h-screen flex flex-col bg-background">
    {/* Skeleton header */}
    <header className="bg-card shrink-0 px-4 py-3 flex items-center gap-3 border-b border-border">
      <div className="w-8 h-8 rounded-full bg-secondary animate-pulse" />
      <div className="space-y-1.5">
        <div className="w-24 h-3.5 bg-secondary rounded animate-pulse" />
        <div className="w-16 h-2.5 bg-muted rounded animate-pulse" />
      </div>
    </header>
    {/* Skeleton messages */}
    <div className="flex-1 px-4 py-8">
      <div className="max-w-[720px] mx-auto space-y-6">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-secondary animate-pulse shrink-0" />
          <div className="space-y-2 flex-1 max-w-[70%]">
            <div className="h-4 bg-secondary rounded-full animate-pulse w-full" />
            <div className="h-4 bg-secondary rounded-full animate-pulse w-4/5" />
            <div className="h-4 bg-secondary rounded-full animate-pulse w-3/5" />
          </div>
        </div>
        <div className="flex gap-3 flex-row-reverse">
          <div className="w-8 h-8 rounded-full bg-muted animate-pulse shrink-0" />
          <div className="max-w-[50%]">
            <div className="h-10 bg-muted rounded-2xl animate-pulse w-48" />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-secondary animate-pulse shrink-0" />
          <div className="space-y-2 flex-1 max-w-[65%]">
            <div className="h-4 bg-secondary rounded-full animate-pulse w-full" />
            <div className="h-4 bg-secondary rounded-full animate-pulse w-3/4" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ========================================
// MAIN PAGE
// ========================================

const InterviewChatPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [emailInput, setEmailInput] = useState('');
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);

  const {
    phase,
    interviewerInfo,
    messages,
    isLoading,
    isStarting,
    isStreaming,
    isComplete,
    isConfirming,
    isReviewing,
    isGeneratingReview,
    isNavigating,
    isUpdatingReview,
    summary,
    error,
    sessionId,
    totalTopics,
    currentTopicIndex,
    topics,
    topicConfidence,
    reviewSummaries,
    sidebarKnowledge,
    saveCorrection,
    startOrResume,
    sendMessage,
    selectSuggestedAnswer,
    submitMultiSelectAnswers,
    skipTopic,
    navigateToTopic,
    enterReview,
    updateReviewTopic,
    confirmReview,
    backToChat,
    completeInterview,
  } = usePublicInterview(token || '');

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [highlightedDelta, setHighlightedDelta] = useState<KnowledgeDelta | null>(null);
  const [highlightKey, setHighlightKey] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const currentTopicId = topics[currentTopicIndex]?.id ?? null;
  const hasSidebarKnowledge = !!(
    sidebarKnowledge &&
    (sidebarKnowledge.company_summary.name ||
      Object.keys(sidebarKnowledge.topics).length > 0)
  );

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resume from sessionStorage if email was previously submitted
  useEffect(() => {
    if (interviewerInfo && !sessionId && phase === 'email' && !error && !hasSubmittedEmail) {
      const savedEmail = sessionStorage.getItem(`interview-email-${token}`);
      if (savedEmail) {
        setHasSubmittedEmail(true);
        startOrResume(savedEmail);
      }
    }
  }, [interviewerInfo, sessionId, phase, error, hasSubmittedEmail, token, startOrResume]);

  const handleEmailSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = emailInput.trim();
      if (trimmed) {
        setHasSubmittedEmail(true);
        startOrResume(trimmed);
      }
    },
    [emailInput, startOrResume]
  );

  // Loading state — skeleton
  if (isLoading) {
    return <ChatSkeleton />;
  }

  // Error state
  if (error && !interviewerInfo) {
    return (
      <div className="h-screen flex flex-col bg-background">
        <ChatHeader logoUrl={interviewerInfo?.logoUrl} />
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={easeOut}
            className="text-center max-w-md px-4"
          >
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <HobbesAvatar size="md" logoUrl={interviewerInfo?.logoUrl} />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-1">
              Interview Not Found
            </h2>
            <p className="text-sm text-muted-foreground">
              {error || 'This interview link may have expired or is no longer active.'}
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Email capture form — shown before interview starts
  if (!sessionId && !isStarting && interviewerInfo && !hasSubmittedEmail) {
    const topicNames = interviewerInfo.topics?.map((t) => t.name) || [];
    const estimatedMinutes = Math.max(5, (interviewerInfo.total_topics || 1) * 3);

    return (
      <div className="h-screen flex flex-col bg-background">
        <ChatHeader name={interviewerInfo.name} logoUrl={interviewerInfo.logoUrl} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-lg">
            <motion.div
              initial="initial"
              animate="animate"
              variants={{ animate: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } } }}
              className="flex flex-col items-center"
            >
              {/* Avatar */}
              <motion.div
                variants={{ initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1, transition: easeOut } }}
                className="mb-6"
              >
                <HobbesAvatar size="lg" logoUrl={interviewerInfo.logoUrl} />
              </motion.div>

              {/* Title */}
              <motion.h1
                variants={{ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0, transition: easeOut } }}
                className="text-2xl font-semibold text-foreground tracking-tight text-center"
              >
                {interviewerInfo.name || 'Hobbes Interview'}
              </motion.h1>

              {/* Description */}
              <motion.p
                variants={{ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0, transition: easeOut } }}
                className="text-sm text-muted-foreground mt-2 text-center max-w-sm leading-relaxed"
              >
                {interviewerInfo.description ||
                  "We'll ask a few questions to understand your product and set up your Hobbes agent."}
              </motion.p>

              {/* Meta: time + topic count */}
              <motion.div
                variants={{ initial: { opacity: 0 }, animate: { opacity: 1, transition: { duration: 0.4 } } }}
                className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground/70"
              >
                <Clock className="w-3.5 h-3.5" />
                <span>~{estimatedMinutes} min</span>
                <span className="mx-1">&middot;</span>
                <span>{interviewerInfo.total_topics || topicNames.length} topics</span>
              </motion.div>

              {/* Topic pills — stagger individually */}
              {topicNames.length > 0 && (
                <motion.div
                  variants={{ initial: {}, animate: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } } }}
                  className="flex flex-wrap justify-center gap-1.5 mt-4 max-w-sm"
                >
                  {topicNames.map((name) => (
                    <motion.span
                      key={name}
                      variants={{ initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } } }}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-secondary text-muted-foreground"
                    >
                      {name}
                    </motion.span>
                  ))}
                </motion.div>
              )}

              {/* Divider */}
              <motion.div
                variants={{ initial: { opacity: 0, scaleX: 0 }, animate: { opacity: 1, scaleX: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } } }}
                className="w-full max-w-xs h-px bg-border mt-7 mb-6 origin-center"
              />

              {/* Email form */}
              <motion.form
                variants={{ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0, transition: easeOut } }}
                onSubmit={handleEmailSubmit}
                className="w-full max-w-xs space-y-3"
              >
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
                  <input
                    type="email"
                    required
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-lg border border-input pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 bg-background transition-colors"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!emailInput.trim()}
                >
                  Begin Interview
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.form>

              {/* Returning user hint */}
              <motion.p
                variants={{ initial: { opacity: 0 }, animate: { opacity: 1, transition: { delay: 0.5 } } }}
                className="text-[11px] text-muted-foreground/50 mt-4 text-center"
              >
                Returning? Enter the same email to pick up where you left off.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Starting/loading state
  if (isStarting || (!sessionId && !error)) {
    return (
      <div className="h-screen flex flex-col bg-background">
        <ChatHeader name={interviewerInfo?.name} logoUrl={interviewerInfo?.logoUrl} />
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="text-center"
          >
            <HobbesAvatar size="lg" logoUrl={interviewerInfo?.logoUrl} />
            <p className="text-sm text-muted-foreground mt-4 animate-pulse">
              Setting up your interview...
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  // Find last interviewer message for showing suggested chips
  const lastInterviewerMessageIndex = messages.reduce(
    (lastIdx, msg, idx) => (msg.speaker === 'interviewer' ? idx : lastIdx),
    -1
  );

  return (
    <div className="h-screen flex flex-col bg-background">
      <ChatHeader
        name={interviewerInfo?.name}
        logoUrl={interviewerInfo?.logoUrl}
        totalTopics={totalTopics}
        currentTopicIndex={currentTopicIndex}
        topics={topics}
        topicConfidence={topicConfidence}
        onNavigateToTopic={navigateToTopic}
        onComplete={enterReview}
        onSkipTopic={skipTopic}
        isNavigating={isNavigating}
        isComplete={isComplete}
        isStreaming={isStreaming}
        isCompleting={isConfirming}
        isReviewing={isReviewing}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        hasSidebarKnowledge={hasSidebarKnowledge}
      />

      {/* Main content area: three-way phase-based render */}
      <AnimatePresence mode="wait">
        {(phase === 'reviewing' || phase === 'generating_review' || phase === 'confirming') ? (
          <ReviewView
            key="review"
            summaries={reviewSummaries}
            topicConfidence={topicConfidence}
            onUpdateTopic={updateReviewTopic}
            onConfirm={confirmReview}
            onBackToChat={backToChat}
            isGenerating={isGeneratingReview}
            isUpdating={isUpdatingReview}
            isConfirming={isConfirming}
            interviewerName={interviewerInfo?.name}
            logoUrl={interviewerInfo?.logoUrl}
          />
        ) : (phase === 'complete') ? (
          <CompletionView
            key="completion"
            summary={summary}
            isCompleting={isConfirming}
            onComplete={completeInterview}
            alreadyComplete={isComplete}
          />
        ) : (
          <motion.div
            key="chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex min-h-0"
          >
            {/* Chat column */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Chat area */}
              <div className="flex-1 overflow-y-auto">
                <div className="max-w-[720px] mx-auto px-4 py-6 space-y-4">
                  <AnimatePresence mode="popLayout">
                    {messages.map((msg, idx) => (
                        <MessageBubble
                          key={msg.id}
                          message={msg}
                          logoUrl={interviewerInfo?.logoUrl}
                          isLastMessage={idx === lastInterviewerMessageIndex}
                          onSelectSuggestion={!isStreaming ? selectSuggestedAnswer : undefined}
                          onSubmitMultiSelect={!isStreaming ? submitMultiSelectAnswers : undefined}
                          onViewKnowledgeChanges={(delta) => {
                            setHighlightedDelta(delta);
                            setHighlightKey((k) => k + 1);
                            setSidebarOpen(true);
                          }}
                        />
                    ))}
                  </AnimatePresence>
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Error display */}
              {error && sessionId && (
                <div className="px-4 shrink-0">
                  <div className="max-w-[720px] mx-auto">
                    <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-2 text-sm mb-2">
                      {error}
                    </div>
                  </div>
                </div>
              )}

              <ChatInput onSend={sendMessage} disabled={isStreaming || isNavigating} />
            </div>

            {/* Desktop sidebar */}
            <AnimatePresence>
              {!isMobile && sidebarOpen && hasSidebarKnowledge && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 360, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="shrink-0 overflow-hidden border-l border-border"
                >
                  <KnowledgeSidebar
                    knowledge={sidebarKnowledge}
                    currentTopicId={currentTopicId}
                    onSaveCorrection={saveCorrection}
                    highlightedDelta={highlightedDelta}
                    highlightKey={highlightKey}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Mobile sidebar (Sheet overlay) */}
            {isMobile && (
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetContent side="right" className="w-[85vw] sm:max-w-[360px] p-0">
                  <SheetTitle className="sr-only">Knowledge Sidebar</SheetTitle>
                  <KnowledgeSidebar
                    knowledge={sidebarKnowledge}
                    currentTopicId={currentTopicId}
                    onSaveCorrection={saveCorrection}
                    highlightedDelta={highlightedDelta}
                    highlightKey={highlightKey}
                  />
                </SheetContent>
              </Sheet>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InterviewChatPage;
